import React, { useState } from 'react';
import {
  Stack,
  FormControlLabel,
  Checkbox,
  Typography,
} from '@mui/material';
import { CheckCircle as DeliverIcon } from '@mui/icons-material';
import {
  EasyCheckDialog,
  EasyCheckButton,
  EasyCheckTextField,
  EasyCheckDatePicker,
} from '../../common/Controls';
import { useConfirmDelivery } from '../../../hooks/useChequeCollections';

const DeliveryConfirmDialog = ({ open, cheque, onClose, onSuccess, onError }) => {
  const deliverMutation = useConfirmDelivery();

  const trackingRef = cheque?.TrackingReference || cheque?.trackingReference || '';

  const [form, setForm] = useState({
    deliveredDate: new Date(),
    receivedBy: '',
    signatureObtained: false,
    deliveryReference: trackingRef,
    remarks: '',
  });

  const handleChange = (field) => (e) => {
    const value = e?.target ? e.target.value : e;
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleCheckboxChange = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.checked }));
  };

  const handleSave = async () => {
    if (!form.receivedBy.trim()) {
      onError('Received by name is required');
      return;
    }
    if (!form.signatureObtained) {
      onError('Delivery signature confirmation is required');
      return;
    }

    const collectionId = cheque.Id || cheque.id;
    try {
      const result = await deliverMutation.mutateAsync({
        collectionId,
        deliveredDate: form.deliveredDate?.toISOString() || new Date().toISOString(),
        receivedBy: form.receivedBy.trim(),
        signatureObtained: form.signatureObtained,
        deliveryReference: form.deliveryReference.trim() || null,
        remarks: form.remarks.trim() || null,
      });
      if (result.success) {
        onSuccess(result.message || 'Delivery confirmed successfully');
      } else {
        onError(result.message || 'Failed to confirm delivery');
      }
    } catch (err) {
      onError('Failed to confirm delivery');
    }
  };

  const chequeNumber = cheque?.ChequeNumber || cheque?.chequeNumber || '';
  const beneficiary = cheque?.BeneficiaryName || cheque?.beneficiaryName || '';
  const courier = cheque?.CourierCompany || cheque?.courierCompany || '';

  return (
    <EasyCheckDialog
      open={open}
      onClose={onClose}
      title="Confirm Delivery"
      variant="form"
      titleIcon={<DeliverIcon />}
      actions={
        <>
          <EasyCheckButton buttonType="form" variant="outlined" onClick={onClose}>
            Cancel
          </EasyCheckButton>
          <EasyCheckButton
            buttonType="form"
            onClick={handleSave}
            loading={deliverMutation.isPending}
          >
            Confirm Delivered
          </EasyCheckButton>
        </>
      }
    >
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Cheque <strong>{chequeNumber}</strong> — {beneficiary}
        {courier && <> (via {courier})</>}
      </Typography>

      <Stack spacing={2}>
        <EasyCheckDatePicker
          label="Delivered Date"
          value={form.deliveredDate}
          onChange={handleChange('deliveredDate')}
          slotProps={{ textField: { required: true, fullWidth: true } }}
        />

        <EasyCheckTextField
          label="Received By"
          value={form.receivedBy}
          onChange={handleChange('receivedBy')}
          required
          fullWidth
          placeholder="Name of person at supplier who received"
        />

        <FormControlLabel
          control={
            <Checkbox
              checked={form.signatureObtained}
              onChange={handleCheckboxChange('signatureObtained')}
              color="primary"
            />
          }
          label="Delivery confirmed by recipient"
        />

        <EasyCheckTextField
          label="Delivery Reference"
          value={form.deliveryReference}
          onChange={handleChange('deliveryReference')}
          fullWidth
          placeholder="Tracking or waybill number"
        />

        <EasyCheckTextField
          label="Remarks"
          value={form.remarks}
          onChange={handleChange('remarks')}
          fullWidth
          multiline
          rows={2}
          placeholder="Any notes (optional)"
        />
      </Stack>
    </EasyCheckDialog>
  );
};

export default DeliveryConfirmDialog;
